<template>
  <!-- Show ORANGE pill for actively waiting customers (WAITING or READY_NEXT) -->
  <vs-chip
    v-if="value && refreshIntervalId"
    class="cursor-pointer"
    style="height: 26px"
    color="warning"
  >
    <feather-icon
      icon="ClockIcon"
      svgClasses="h-5 w-5 mr-1" />
    <span>
      {{ value | time_ago(
        true,
        tenantAccountDetailsTimezone,
        true,
        '< 1 min',
        endTime,
      ) }}
    </span>
  </vs-chip>

  <!-- Show plain text with no pill when timer stops (ACTIVE/Serving, ATTENDED/Done, CANCELED) -->
  <div
    v-else-if="value"
    class="flex items-center"
  >
    <feather-icon
      icon="ClockIcon"
      svgClasses="h-5 w-5 mr-1" />
    <span>
      {{ value | time_ago(
        true,
        tenantAccountDetailsTimezone,
        true,
        '< 1 min',
        endTime,
      ) }}
    </span>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  name: 'WaitingListCellRendererWaiting',
  props: {
    isAgGridComponent: {
      type: Boolean,
      default: true,
    },
    createdAt: {
      type: String,
      default: '',
    },
    customerPayload: {
      type: [Object, null],
      default: () => null,
    },
  },
  data() {
    return {
      now: Date.now(),
      refreshIntervalId: null,
      hasIntervalEverRun: false, // Track if interval has ever started
      visibilityChangeHandler: null, // Store handler reference for cleanup
    };
  },
  computed: {
    ...mapGetters({
      tenantAccountDetailsTimezone: 'auth/tenantAccountDetailsTimezone',
    }),
    customerData() {
      if (this.customerPayload) {
        return this.customerPayload;
      }

      return this.params && this.params.data
        ? this.params.data
        : null;
    },
    value() {
      if (this.createdAt) return this.createdAt;
      return (this.params && this.params.value ? this.params.value : null);
    },
    status() {
      return this.customerData && this.customerData.status
        ? this.customerData.status
        : null;
    },
    updatedAt() {
      return this.customerData && this.customerData.updatedAt
        ? this.customerData.updatedAt
        : null;
    },
    endTime() {
      // Priority order for determining end time:
      // 1. If timer is running: use this.now (updates every 60s)
      // 2. If we have client-captured stop time: use it (persists across tab changes)
      // 3. If interval has run in this session: use this.now (just stopped)
      // 4. Fallback: use updatedAt from backend
      
      if (this.refreshIntervalId !== null) {
        // Timer is running - use live time
        return new Date(this.now);
      }
      
      if (this.customerData && this.customerData.clientCapturedStopTime) {
        // Use stored client-captured time (persists when component recreates)
        return new Date(this.customerData.clientCapturedStopTime);
      }
      
      if (this.hasIntervalEverRun) {
        // Timer just stopped in this session - use captured time
        return new Date(this.now);
      }
      
      // Fallback to backend time
      return new Date(this.updatedAt);
    },
  },
  watch: {
    status() {
      const isWaitingStatus = this.status === this.$enums.WaitingList.STATUS.WAITING
        || this.status === this.$enums.WaitingList.STATUS.READY_NEXT;

      if (isWaitingStatus && !this.refreshIntervalId) {
        this.startRefreshInterval();
      } else if (!isWaitingStatus && this.refreshIntervalId) {
        this.stopRefreshInterval();
      }
    },
  },
  mounted() {
    if (this.status === this.$enums.WaitingList.STATUS.WAITING
      || this.status === this.$enums.WaitingList.STATUS.READY_NEXT
    ) {
      this.startRefreshInterval();
    }

    // Set up visibility change listener to pause/resume timer
    this.visibilityChangeHandler = () => {
      if (document.hidden) {
        // Tab became hidden - pause timer to save resources
        if (this.refreshIntervalId) {
          clearInterval(this.refreshIntervalId);
          this.refreshIntervalId = null;
        }
      } else {
        // Tab became visible - resume timer if should be running
        const isWaitingStatus = this.status === this.$enums.WaitingList.STATUS.WAITING
          || this.status === this.$enums.WaitingList.STATUS.READY_NEXT;
        
        if (isWaitingStatus && !this.refreshIntervalId) {
          this.now = Date.now(); // Update immediately to current time
          this.startRefreshInterval();
        }
      }
    };
    
    document.addEventListener('visibilitychange', this.visibilityChangeHandler);
  },
  beforeDestroy() {
    if (this.refreshIntervalId) {
      this.stopRefreshInterval();
    }
    
    // Clean up visibility change listener
    if (this.visibilityChangeHandler) {
      document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
    }
  },
  methods: {
    startRefreshInterval() {
      this.hasIntervalEverRun = true; // Mark that interval has started
      this.now = Date.now(); // Update immediately when starting
      this.refreshIntervalId = setInterval(() => {
        this.now = Date.now();
      }, 60000); // Update every 60 seconds (1 minute)
    },
    stopRefreshInterval() {
      this.now = Date.now(); // Capture exact current time before stopping
      
      // Store captured time in customer data so it persists across component recreations
      if (this.customerData && this.params) {
        const updatedData = {
          ...this.customerData,
          clientCapturedStopTime: this.now, // Store client-side captured stop time
        };
        this.params.node.setData(updatedData);
      }
      
      clearInterval(this.refreshIntervalId);
      this.refreshIntervalId = null;
    },
  },
};
</script>
